import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { AlertTriangle, Archive, Boxes, Hourglass, IndianRupee, PackageMinus, Ruler } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, formatINRShort } from "@/lib/api";
import { useAuth, hasRole } from "@/context/AuthContext";
import { viewFor } from "@/config/roleViews";
import { Button } from "@/components/ui/button";
import { useLiveRefresh } from "@/lib/live";
import JobLink from "@/components/JobLink";
import { QueueNo } from "@/components/Shared";
import { EmptyRow, Kpi, Panel, Pill, RollsLink, currentFy, daysLate, money, qty } from "@/components/stores/storesUi";
import { IssueDialog } from "@/components/stores/StoreDialogs";

const monthLabel = (ym) => new Date(`${ym}-01T00:00:00`).toLocaleString("en-IN", { month: "short" });

// Monthly movement rows (DASH BOARD: GOODS RECEIPT L/K/N and GOODS ISSUE H/L/M by month).
const MOVEMENT_ROWS = [
  { key: "purchase_qty", label: "Purchase", unit: "m + kg" },
  { key: "purchase_sqm", label: "Purchase", unit: "SQM" },
  { key: "purchase_value", label: "Purchase", unit: "value", money: true },
  { key: "consumption_qty", label: "Consumption", unit: "m + kg" },
  { key: "consumption_sqm", label: "Consumption", unit: "SQM" },
  { key: "consumption_value", label: "Consumption", unit: "value", money: true },
];

export default function StoresDashboard() {
  const { user } = useAuth();
  const canIssue = hasRole(user, "production_manager");
  const [d, setD] = useState(null);
  const [queue, setQueue] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [issueFor, setIssueFor] = useState(null);

  const load = (quiet = false) =>
    Promise.all([
      api.get("/stores/dashboard"),
      api.get("/stores/jobs", { params: { state: "pending", fy: currentFy(), limit: 6 } }),
    ])
      .then(([dash, jobs]) => { setD(dash.data); setQueue(jobs.data); setUpdatedAt(new Date()); })
      .catch((e) => !quiet && toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Stock moves and job changes (new BPRs, due dates, status) both reshape the queue.
  const reload = useLiveRefresh(["stores", "jobs"], () => load(true));

  if (!d) return <div className="label-caps p-4">Loading…</div>;
  const k = d.kpis;
  // Value figures are only sent to roles with finance access.
  const showMoney = viewFor(user).finance && k.stock_value != null;
  const fyLabel = `FY 20${d.fy.slice(0, 2)}-${d.fy.slice(3)}`;
  const movementRows = MOVEMENT_ROWS.filter((r) => showMoney || !r.money);

  return (
    <div className="space-y-4" data-testid="stores-dashboard">
      <div className="flex items-center justify-end gap-2 text-[11px] text-neutral-500" data-testid="stores-dash-updated">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Live · updated {updatedAt?.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
      </div>
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Link to="/inventory?tab=stock" className="block">
          <Kpi label="Items" value={qty(k.total_items, 0)} icon={Boxes} sub={`${qty(k.total_items - k.reorder_items, 0)} OK`} testid="sd-total-items" />
        </Link>
        <Link to="/inventory?tab=stock" className="block">
          <Kpi label="Closing stock" value={qty(k.closing_qty, 0)} sub="metres + kg" icon={Ruler} testid="sd-closing-m" />
        </Link>
        <Link to="/inventory?tab=stock" className="block">
          <Kpi label="Closing stock" value={qty(k.closing_sqm, 0)} sub="square metres" icon={Ruler} testid="sd-closing-sqm" />
        </Link>
        {showMoney ? (
          <Link to="/inventory?tab=stock" className="block">
            <Kpi label="Stock value" value={formatINRShort(k.stock_value)} sub={money(k.stock_value)} icon={IndianRupee} testid="sd-stock-value" />
          </Link>
        ) : (
          <Link to="/inventory?tab=movements&type=receipt" className="block">
            <Kpi label="Purchase this year" value={qty(k.fy_purchase_sqm, 0)} sub={`SQM · ${fyLabel}`} icon={Archive} />
          </Link>
        )}
      </section>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Link to="/inventory?tab=stock&status=ORDER" className="block">
          <Kpi label="Reorder required" value={qty(k.reorder_items, 0)} sub="at or below min stock" icon={AlertTriangle}
            tone={k.reorder_items ? "danger" : "ok"} testid="sd-reorder" />
        </Link>
        <Link to="/inventory?tab=stock&view=slow" className="block">
          <Kpi label="Slow moving" value={qty(k.slow_moving_items, 0)} sub="no issue in 61–90 days" icon={Hourglass}
            tone={k.slow_moving_items ? "warn" : "ok"} />
        </Link>
        <Link to="/inventory?tab=stock&view=slow" className="block">
          <Kpi label="Non-moving" value={qty(k.non_moving_items, 0)} sub="no issue in 90+ days" icon={Hourglass}
            tone={k.non_moving_items ? "warn" : "ok"} />
        </Link>
        {showMoney ? (
          <Link to="/inventory?tab=movements&type=receipt" className="block">
            <Kpi label="Purchase this year" value={qty(k.fy_purchase_sqm, 0)} sub={`SQM · ${formatINRShort(k.fy_purchase_value)}`} icon={Archive} />
          </Link>
        ) : (
          <Link to="/inventory?tab=movements&type=receipt" className="block">
            <Kpi label="Purchase this month" value={qty(k.month_purchase_sqm, 0)} sub="square metres" icon={Archive} />
          </Link>
        )}
      </section>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="stores-dash-qc">
        <Link to="/inventory?tab=stock" className="block">
          <Kpi label="Usable stock" value={qty(k.usable_qty, 0)} sub="QC released · metres + kg" icon={Ruler} testid="sd-usable" />
        </Link>
        <Link to="/inventory?tab=rolls&qc=PENDING" className="block">
          <Kpi label="Awaiting QC" value={qty(k.qc_pending_rolls, 0)} sub={`rolls · ${qty(k.qc_pending_qty, 0)} m, not issuable yet`} icon={Hourglass}
            tone={k.qc_pending_rolls ? "warn" : "ok"} testid="sd-qc-pending" />
        </Link>
        <Link to={`/inventory?tab=rolls&qc=${encodeURIComponent("NOT APPROVED")}`} className="block">
          <Kpi label="Rejected by QC" value={qty(k.qc_rejected_rolls, 0)} sub={`rolls · ${qty(k.qc_rejected_qty, 0)} m on the shelf`} icon={AlertTriangle}
            tone={k.qc_rejected_rolls ? "danger" : "ok"} testid="sd-qc-rejected" />
        </Link>
      </section>

      <Panel
        title={`Waiting for material · ${qty((queue?.counts.to_issue || 0) + (queue?.counts.partial || 0), 0)}`}
        right={<Link to="/inventory?tab=jobs" className="text-xs text-[#FF5A00] hover:underline">All jobs →</Link>}
        testid="stores-dash-queue"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap">
            <thead className="bg-neutral-50 label-caps text-[10px]">
              <tr>
                <th className="w-10 pl-5 pr-1 py-2 text-right">#</th>
                <th className="text-left pl-3 pr-3 py-2">BPR</th>
                <th className="text-left px-3 py-2">Job</th>
                <th className="text-left px-3 py-2">Due</th>
                <th className="text-left px-3 py-2">Material</th>
                <th className="text-right px-3 py-2">To issue</th>
                <th className="text-right px-3 py-2">In stock</th>
                <th className="text-left px-3 py-2">Stores</th>
                {canIssue && <th className="w-20"></th>}
              </tr>
            </thead>
            <tbody>
              {(queue?.items || []).length === 0 && (
                <EmptyRow cols={canIssue ? 9 : 8} loading={!queue}>Nothing waiting.</EmptyRow>
              )}
              {(queue?.items || []).map((j) => {
                const late = daysLate(j.due_date);
                const short = j.planned_item && j.remaining != null && j.in_stock < j.remaining;
                return (
                  <tr key={j.bpr_no} className="border-t border-neutral-100 data-row">
                    <td className="pl-5 pr-1 py-2 text-right"><QueueNo n={j.queue_no} /></td>
                    <td className="pl-3 pr-3 py-2 font-mono-num text-xs font-semibold">
                      <JobLink id={j.id} number={j.bpr_no} tab="materials" />
                    </td>
                    <td className="px-3 py-2"><div className="truncate max-w-[15rem]" title={j.product_name}>{j.customer_name}</div></td>
                    <td className="px-3 py-2 font-mono-num text-xs">
                      {j.due_date ? String(j.due_date).slice(0, 10) : "—"}
                      {late && <span className="text-rose-700 font-sans"> · {late}d late</span>}
                    </td>
                    <td className="px-3 py-2 font-mono-num text-xs"><div className="truncate max-w-[9rem]">{j.planned_item || j.material || "—"}</div></td>
                    <td className="px-3 py-2 text-right font-mono-num">{j.remaining != null ? `${qty(j.remaining, 0)} m` : "—"}</td>
                    <td className={`px-3 py-2 text-right font-mono-num ${short ? "text-rose-700" : ""}`} title={short ? "Less in stock than is still to issue" : undefined}>
                      {j.in_stock == null ? "—" : `${qty(j.in_stock, 0)} m`}
                    </td>
                    <td className="px-3 py-2"><Pill value={j.stores_state} /></td>
                    {canIssue && (
                      <td className="pl-1 pr-5 py-2 text-right">
                        <Button size="sm" variant="outline" className="rounded-sm h-7 px-2.5 gap-1" onClick={() => setIssueFor(j.bpr_no)} data-testid={`dash-issue-${j.bpr_no}`}>
                          <PackageMinus size={12} /> Issue
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <div className="space-y-4">
          <Panel title={`This month · to ${d.as_of}`}>
            <table className="w-full text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap" data-testid="sd-this-month">
              <thead className="bg-neutral-50 label-caps text-[10px]">
                <tr>
                  <th className="text-left px-5 py-2"></th>
                  <th className="text-right px-5 py-2">m + kg</th>
                  <th className="text-right px-5 py-2">SQM</th>
                  {showMoney && <th className="text-right px-5 py-2">Value</th>}
                </tr>
              </thead>
              <tbody>
                {[
                  ["Purchase", k.month_purchase_qty, k.month_purchase_sqm, k.month_purchase_value],
                  ["Consumption", k.month_consumption_qty, k.month_consumption_sqm, k.month_consumption_value],
                ].map(([label, q, sqm, value]) => (
                  <tr key={label} className="border-t border-neutral-100">
                    <td className="px-5 py-2.5 font-medium">{label}</td>
                    <td className="px-5 py-2.5 text-right font-mono-num">{qty(q)}</td>
                    <td className="px-5 py-2.5 text-right font-mono-num">{qty(sqm)}</td>
                    {showMoney && <td className="px-5 py-2.5 text-right font-mono-num" title={money(value)}>{formatINRShort(value)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {showMoney && (
              <div className="px-5 py-3 border-t border-neutral-200 flex justify-between text-xs text-neutral-500">
                <span>Consumption value to date</span>
                <span className="font-mono-num text-neutral-900" title={money(k.consumption_value_total)}>{formatINRShort(k.consumption_value_total)}</span>
              </div>
            )}
          </Panel>

          <Panel title="Purchase vs consumption · SQM">
            <div className="h-72 p-4" data-testid="stores-dash-chart">
              <ResponsiveContainer>
                <BarChart data={d.monthly.map((m) => ({ ...m, label: monthLabel(m.month) }))}>
                  <CartesianGrid stroke="#eee" strokeDasharray="2 2" vertical={false} />
                  <XAxis dataKey="label" stroke="#71717A" fontSize={11} />
                  <YAxis stroke="#71717A" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => `${qty(v, 0)} SQM`} contentStyle={{ borderRadius: 2, border: "1px solid #E4E4E7", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="purchase_sqm" fill="#FF5A00" name="Purchase" isAnimationActive={false} />
                  <Bar dataKey="consumption_sqm" fill="#3F3F46" name="Consumption" isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>
        <Panel
          title="Top 10 · closing stock"
          right={<Link to="/inventory?tab=stock" className="text-xs text-[#FF5A00] hover:underline">Stock report →</Link>}
        >
          <div className="divide-y divide-neutral-100" data-testid="stores-dash-top10">
            {d.top_items.map((t, i) => (
              <div key={t.item_code} className="px-5 py-2 flex items-center justify-between gap-3">
                <div className="min-w-0 flex items-center gap-3">
                  <span className="font-mono-num text-[11px] text-neutral-400 w-4 text-right">{i + 1}</span>
                  <div className="min-w-0">
                    <RollsLink q={t.item_code} className="font-mono-num text-xs font-semibold" />
                    <div className="text-xs text-neutral-500 truncate">{t.material_name}</div>
                  </div>
                </div>
                <div className="font-mono-num text-sm whitespace-nowrap">{qty(t.closing, 0)} {t.uom === "KG" ? "kg" : "m"}</div>
              </div>
            ))}
          </div>
        </Panel>
      </section>

      <Panel title={`Monthly movement · ${fyLabel}`} testid="stores-dash-monthly">
        <div className="overflow-x-auto">
        <table className="w-full text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap">
          <thead className="bg-neutral-50 label-caps text-[10px]">
            <tr>
              <th className="text-left px-4 py-2.5"></th>
              {d.monthly.map((m) => <th key={m.month} className="text-right px-3 py-2.5">{monthLabel(m.month)}</th>)}
            </tr>
          </thead>
          <tbody>
            {movementRows.map((r, i) => (
              <tr key={r.key} className={`data-row ${i && r.label !== movementRows[i - 1].label ? "border-t-2 border-neutral-200" : "border-t border-neutral-100"}`}>
                <td className="px-4 py-2 whitespace-nowrap">
                  <span className="font-medium">{r.label}</span>
                  <span className="text-xs text-neutral-500"> · {r.unit}</span>
                </td>
                {d.monthly.map((m) => (
                  <td key={m.month} className={`px-3 py-2 text-right font-mono-num ${m[r.key] ? "" : "text-neutral-300"}`}>
                    {r.money ? (m[r.key] ? <span title={money(m[r.key])}>{formatINRShort(m[r.key])}</span> : "—") : m[r.key] ? qty(m[r.key], 0) : "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Panel>

      {issueFor && (
        <IssueDialog initialBpr={issueFor} onClose={() => setIssueFor(null)} onDone={() => { setIssueFor(null); reload(); }} />
      )}
    </div>
  );
}
